import { SkipNav, Spinner } from '@cmsgov/design-system';
import { Outlet, useLocation, useParams } from 'react-router-dom';

import Tabs from '../../components/Tabs';
import { ContractProvider, useContractData } from './context';
import { ContractTab } from './types';
import './Contract.css';
import { CONTRACT_ROLE, useRBAC } from '@/hooks/useRBAC';
import { ROUTES } from '@/constants';

function ContractContent() {
  const { contractId, contract, isLoading, isError, error } = useContractData();
  const location = useLocation();
  const { hasRoleOnContract, isAdmin, hasAnyRoleOnContract } = useRBAC();
  const baseTestId = 'contract-page';
  const canViewContractSettings = isAdmin || hasRoleOnContract(contractId ?? '', CONTRACT_ROLE.COR);
  const canViewRosterTab =
    isAdmin || hasAnyRoleOnContract(contractId ?? '', [CONTRACT_ROLE.COR, CONTRACT_ROLE.ROSTER_OWNER]);
  const basePath = `/contract/${contractId}`;

  // Contract routes render under RequireAuth, so RBAC reflects initialized auth state.
  if (!canViewContractSettings && location.pathname === `${basePath}${ROUTES.CONTRACT_TABS.SETTINGS}`) {
    throw new Response('Not Found', { status: 404 });
  }

  if (!canViewRosterTab && location.pathname === `${basePath}${ROUTES.CONTRACT_TABS.ROSTER}`) {
    throw new Response('Not Found', { status: 404 });
  }

  if (isLoading) {
    return (
      <>
        <SkipNav href='#content'>Skip to page content</SkipNav>
        <div
          className='ds-l-row'
          data-testid={baseTestId}>
          <article
            aria-labelledby='page-heading'
            className='ds-content ds-l-col--12'
            data-testid={`${baseTestId}-content`}
            id='content'>
            <div
              className='ds-u-padding--3'
              data-testid={`${baseTestId}-loading`}>
              <div data-testid={`${baseTestId}-loading-text`}>
                <Spinner aria-valuetext='Loading contract...' />
              </div>
            </div>
          </article>
        </div>
      </>
    );
  }

  if (isError || !contract) {
    return (
      <>
        <SkipNav href='#content'>Skip to page content</SkipNav>
        <div
          className='ds-l-row'
          data-testid={baseTestId}>
          <article
            aria-labelledby='page-heading'
            className='ds-content ds-l-col--12'
            data-testid={`${baseTestId}-content`}
            id='content'>
            <div
              className='ds-u-padding--3'
              data-testid={`${baseTestId}-error`}>
              <h1
                data-testid={`${baseTestId}-error-heading`}
                id='page-heading'>
                Contract Not Found
              </h1>
              <p data-testid={`${baseTestId}-error-message`}>
                {error?.message || `Contract ${contractId} could not be found.`}
              </p>
            </div>
          </article>
        </div>
      </>
    );
  }

  const outlet = <Outlet />;
  const tabContent = (path: string) => (location.pathname.includes(path) ? outlet : null);

  const tabs = [
    {
      content: tabContent(ROUTES.CONTRACT_TABS.DETAILS),
      id: ContractTab.DETAILS,
      label: 'Contract Details',
      path: ROUTES.CONTRACT_TABS.DETAILS,
    },
    ...(canViewContractSettings
      ? [
          {
            content: tabContent(ROUTES.CONTRACT_TABS.SETTINGS),
            id: ContractTab.SETTINGS,
            label: 'Contract Settings',
            path: ROUTES.CONTRACT_TABS.SETTINGS,
          },
        ]
      : []),
    {
      content: tabContent(ROUTES.CONTRACT_TABS.ROSTER_OWNERS),
      id: ContractTab.ROSTER_OWNERS,
      label: 'Roster Owners',
      path: ROUTES.CONTRACT_TABS.ROSTER_OWNERS,
    },
    {
      content: tabContent(ROUTES.CONTRACT_TABS.LABOR_CATEGORIES),
      id: ContractTab.LABOR_CATEGORIES,
      label: 'Labor Categories',
      path: ROUTES.CONTRACT_TABS.LABOR_CATEGORIES,
    },
    ...(canViewRosterTab
      ? [
          {
            content: tabContent(ROUTES.CONTRACT_TABS.ROSTER),
            id: ContractTab.ROSTER,
            label: 'Roster',
            path: ROUTES.CONTRACT_TABS.ROSTER,
          },
        ]
      : []),
  ];

  return (
    <>
      <SkipNav href='#content'>Skip to page content</SkipNav>
      <div
        className='ds-l-row'
        data-testid={baseTestId}>
        <article
          aria-labelledby='page-heading'
          className='ds-content ds-l-col--12'
          data-testid={`${baseTestId}-content`}
          id='content'>
          <h1
            data-testid={`${baseTestId}-heading`}
            id='page-heading'>
            Contract {contract.contractNumber}, {contract.companyName}
          </h1>

          <Tabs
            basePath={basePath}
            data-testid={`${baseTestId}-tabs`}
            tabs={tabs}
          />
        </article>
      </div>
    </>
  );
}

function Contract() {
  const { id } = useParams<{ id: string }>();
  const baseTestId = 'contract-page';

  if (!id) {
    return (
      <div
        className='ds-l-row'
        data-testid={baseTestId}>
        <article
          className='ds-content ds-l-col--12'
          data-testid={`${baseTestId}-content`}>
          <div
            className='ds-u-padding--3'
            data-testid={`${baseTestId}-missing`}>
            <p data-testid={`${baseTestId}-missing-text`}>No contract specified.</p>
          </div>
        </article>
      </div>
    );
  }

  return (
    <ContractProvider contractId={id}>
      <ContractContent />
    </ContractProvider>
  );
}

export default Contract;
